import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18nObjectHook } from "../../../../../../../../../../i18n";
import { getCommonClass, hasDifferentClasses } from "./utils";
import { SelectedTextPosition } from "../../FormattingComp";
import { SubscribeToExtendedPage_extendedPage_customCss } from "../../../../../../../api/__generated__/SubscribeToExtendedPage";
import { useCreateCssFromPosition } from "../../../../../../../api/metadata/dynamic/customCss";

type Args = {
  setVisibility: (value: boolean) => void;
  selectedTextPosition?: SelectedTextPosition;
  classesMetadata: SubscribeToExtendedPage_extendedPage_customCss[];
  projectId: string;
  stringVersion: number;
};

export const useModal = ({
  setVisibility,
  selectedTextPosition = { start: 0, size: 0 },
  classesMetadata,
  projectId,
  stringVersion,
}: Args) => {
  /**
   * State
   */
  const [value, setValue] = useState(() => getCommonClass(selectedTextPosition, classesMetadata));
  const isOverrideWarning = hasDifferentClasses(selectedTextPosition, classesMetadata);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const strings = useI18nObjectHook("project.existing.editingMode.formatting.modalCustomCss");

  /**
   * Methods
   */
  const close = useCallback(() => setVisibility(false), [setVisibility]);

  /**
   * Requests
   */
  const [applyCreateCssFromPosition, { loading: createCssLoading, error }] =
    useCreateCssFromPosition();

  /**
   * Handlers
   */
  const onChange = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    // @ts-ignore // TODO LOIC CHECK
    setValue(event.target.value);
  }, []);

  const onCancel = useCallback(() => {
    close();
    setValue("");
  }, [close]);

  const onOk = useCallback(async () => {
    if (pending.current) {
      return;
    }
    pending.current = true;
    try {
      await applyCreateCssFromPosition({
        variables: {
          projectId: projectId,
          start: selectedTextPosition.start,
          size: selectedTextPosition.size,
          stringVersion: stringVersion,
          newCss: {
            customCssClasses: value?.trim(),
          },
        },
      });
      if (mounted.current) close();
    } catch {
      // Apollo exposes the error below; keep this draft open for retry.
      return;
    } finally {
      pending.current = false;
    }
  }, [
    applyCreateCssFromPosition,
    projectId,
    selectedTextPosition.start,
    selectedTextPosition.size,
    stringVersion,
    value,
    close,
  ]);

  return {
    inputValue: value,
    onInputChange: onChange,
    onOk,
    onCancel,
    strings,
    isOverrideWarning,
    loading: createCssLoading,
    error: error?.message,
  };
};
